// WHICH ITEM OF AN OPEN RESEARCH PACK IS SELECTED — one answer, read by both sides of the page
// (busy-my-web#605, Saffet 2026-10-09).
//
//   "When you click something from the timeline the research pack shows you in purple where it
//    originated from in the research pack. Also can we click the text and links from in the
//    research pack so it is two way match from the timeline or the research pack."
//
// The rail → document direction already existed (a tap on a "Research pack Timeline" card scrolls
// the document to the item and frames it — StoryView's scrollDocToItem). The way back is a click on
// an extraction in the document lighting up ITS card in the rail. Both directions write the same
// `{ packId, itemId }` (ongoing store, `packSelection`) and both colour it with the same token below,
// so the two sides can never name different items or draw them in different purples.
//
// Identity is the one the rail → document jump already uses: an item's place in the document is
// `findItemAnchor` (its picture, its link line, its words), so a clicked block belongs to the item
// whose anchor it IS. Nothing new is guessed here — a click resolves to exactly the item a tap on that
// item's card would have scrolled to.

import { findItemAnchor } from '@/lib/packDocAnchor'

// The pack's selection purple — the landing highlight's colours (`.doc-flash-box`), shared so the
// rail card and the document draw one colour.
export const PACK_SELECT_TINT = {
  border: 'rgba(55, 65, 243, 0.8)',
  fill: 'rgba(55, 65, 243, 0.16)',
  ring: 'rgba(55, 65, 243, 0.12)',
}

/** The inline style a selected item wears, in the document's flash and on its rail card alike. */
export const packSelectStyle = () => ({
  borderColor: PACK_SELECT_TINT.border,
  backgroundColor: PACK_SELECT_TINT.fill,
  boxShadow: `0 0 0 4px ${PACK_SELECT_TINT.ring}`,
})

/**
 * The selection for `itemId` inside `packId`, or null when either is missing — the store keeps
 * exactly this shape, so a selection always says which pack it belongs to.
 * @param {string|null} packId
 * @param {string|null} itemId
 * @returns {{ packId: string, itemId: string } | null}
 */
export function packSelection(packId, itemId) {
  if (!packId || !itemId) return null
  return { packId: String(packId), itemId: String(itemId) }
}

/**
 * The selected item's id in the pack that is open, or null. A selection left over from another pack
 * reads as nothing, so a card in this pack's rail is never lit by a click made in a different one.
 * @param {{ packId?: string, itemId?: string } | null} sel
 * @param {string|null} packId the pack open right now
 * @returns {string|null}
 */
export function selectedPackItemId(sel, packId) {
  if (!sel || !packId || !sel.itemId) return null
  return String(sel.packId) === String(packId) ? String(sel.itemId) : null
}

/** Is `itemId` the selected item of the open pack? */
export const isPackItemSelected = (sel, packId, itemId) =>
  !!itemId && selectedPackItemId(sel, packId) === String(itemId)

/**
 * What a click inside the pack document means for the selection.
 *
 *  - `{ skip: true }` — leave the selection alone: the click was on a LINK (it navigates, and that is
 *    all it does — Saffet asked for links to keep working) or outside the document (the callout
 *    cards and other overlays answer for themselves).
 *  - `{ itemId }` — the item the clicked block shows, by the same anchor rule as the rail's jump.
 *  - `{ itemId: null }` — a click on writing that is no pack item (a heading, the user's own text):
 *    the selection clears, so the rail never claims the reader is on an item they are not on.
 *
 * @param {Element|null} root the editor's rendered document
 * @param {EventTarget|null} target the click's target
 * @param {Array<object>} items the pack's research items ({ id, kind, url, content, title })
 * @returns {{ skip: true } | { itemId: string|null }}
 */
export function docClickSelection(root, target, items) {
  if (!root || !target || typeof target.closest !== 'function' || !root.contains(target)) return { skip: true }
  if (target.closest('a')) return { skip: true }
  for (const item of items || []) {
    if (!item?.id) continue
    const anchor = findItemAnchor(root, item)
    if (anchor && (anchor === target || anchor.contains(target))) return { itemId: String(item.id) }
  }
  return { itemId: null }
}

/**
 * Bring a rail card into view without jumping the panel when it is already showing — `nearest`.
 * jsdom (and any element without layout) has no scrollIntoView; that is not an error here.
 * @param {Element|null} el
 * @returns {boolean} whether a scroll was asked for
 */
export function revealRailCard(el) {
  if (!el || typeof el.scrollIntoView !== 'function') return false
  el.scrollIntoView({ block: 'nearest' })
  return true
}
